import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Crosshair,
  KeyRound,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Info,
  Target,
  Lightbulb,
  FileText,
  ListChecks,
  Copy,
  Check,
} from 'lucide-react';
import {
  Card,
  CardTitle,
  CardDescription,
  Input,
  Button,
  Skeleton,
} from '@/components/ui';
import { ScoreBadge } from '@/components/keyword';
import { api } from '@/lib/api';
import { formatNumber, type SerpItem } from '@keywordgod/shared';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';
import { ApiKeyGate } from '@/components/ApiKeyGate';

/**
 * Phase 39: 1page 점유 분석 + 빈자리 진단
 * 키워드 입력 → 우리 키 풀로 Naver SERP 가져옴 → 사용자 Gemini 키로 분석
 */
export function SerpAnalysis() {
  const prefs = useUserPrefs();
  const [keyword, setKeyword] = useState('');
  const hasKey = !!prefs.geminiApiKey;

  const m = useMutation({
    mutationFn: () =>
      api.serpAnalysis.analyze({
        keyword: keyword.trim(),
        geminiApiKey: prefs.geminiApiKey.trim(),
      }),
  });

  const canSubmit = !!keyword.trim() && hasKey && !m.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Crosshair className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">1page 점유 분석</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            BETA
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          이 키워드 1page 가 어떤 글로 채워져 있는지 직접 보고, AI 가 "빠뜨린 각도"를 찾아 1위 후보 글 제목을 추천합니다.
        </p>
      </header>

      <ApiKeyGate hasKey={hasKey} />

      {hasKey && (
      <Card>
        <CardTitle className="flex items-center gap-2">
          <Crosshair className="h-5 w-5 text-gold" /> 분석할 키워드
        </CardTitle>
        <CardDescription>
          키워드만 입력하세요. Gemini 키는 설정에서 자동 사용됩니다.
        </CardDescription>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) m.mutate();
          }}
        >
          <label className="block">
            <span className="text-xs text-gray-400">키워드</span>
            <Input
              className="mt-1"
              placeholder="예: 칼륨 많은 음식"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              autoComplete="off"
            />
          </label>
          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" variant="gold" disabled={!canSubmit}>
              {m.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> 분석 중…
                </>
              ) : (
                <>
                  <Crosshair className="h-4 w-4" /> 1page 분석 시작
                </>
              )}
            </Button>
            {m.isPending && (
              <span className="text-xs text-gray-500">
                Naver SERP 수집 → Gemini 진단 (~5–15초)
              </span>
            )}
          </div>
        </form>

        {m.isError && (
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="inline h-4 w-4 mr-1" />
            {(m.error as Error).message}
          </p>
        )}
      </Card>
      )}

      {m.isPending && <LoadingSkeleton />}
      {m.data && <ResultSection data={m.data} />}
    </div>
  );
}

// ─── 로딩 스켈레톤 ───────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <Skeleton className="h-6 w-64" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </Card>
      <Card>
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── 결과 ────────────────────────────────────────────────────

interface AnalyzeResult {
  keyword: string;
  meta: {
    godScore: number | null;
    grade: string | null;
    totalSearch: number | null;
    saturation: number | null;
    compIdx: string | null;
    blogTotal: number | null;
    inOurDb: boolean;
  };
  serp: {
    blog: SerpItem[];
    cafe: SerpItem[];
    news: SerpItem[];
  };
  analysis: {
    commonTopics: string[];
    missingAngles: string[];
    topRecommendedTitles: string[];
    rivalKeywords: Array<{ keyword: string; reason: string }>;
    diagnosis: string;
  };
}

function ResultSection({ data }: { data: AnalyzeResult }) {
  return (
    <div className="space-y-4">
      {/* 진단 + 메타 */}
      <Card className="border-gold/40 bg-gradient-to-br from-gold/10 to-transparent">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-gold" />
            <span>"{data.keyword}" 시장 진단</span>
            {data.meta.grade && <ScoreBadge grade={data.meta.grade as never} size="sm" />}
          </CardTitle>
          {data.meta.inOurDb && (
            <div className="text-xs text-gray-400">
              {data.meta.totalSearch != null && (
                <>월 {formatNumber(data.meta.totalSearch)} · </>
              )}
              God {data.meta.godScore} · 포화 {data.meta.saturation}%
            </div>
          )}
        </div>
        <p className="mt-3 text-sm text-amber-100 leading-relaxed">
          {data.analysis.diagnosis}
        </p>
      </Card>

      {/* 공통 주제 vs 빠뜨린 각도 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4 text-blue-400" />
            1page 공통 주제
          </CardTitle>
          <CardDescription>이미 다 다루는 주제 — 추가로 써봤자 묻힙니다</CardDescription>
          <ul className="mt-3 space-y-1.5">
            {data.analysis.commonTopics.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-blue-400">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="border-emerald-400/40 bg-emerald-400/5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-4 w-4 text-emerald-300" />
            빠뜨린 각도 — 여기 노려라
          </CardTitle>
          <CardDescription>1page 글들이 다루지 않은 차별화 포인트</CardDescription>
          <ul className="mt-3 space-y-1.5">
            {data.analysis.missingAngles.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-100">
                <span className="text-emerald-300">★</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* 추천 글 제목 */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gold" /> 1위 후보 글 제목
          </CardTitle>
          <CopyListButton items={data.analysis.topRecommendedTitles} label="제목 복사" />
        </div>
        <ol className="mt-3 space-y-2">
          {data.analysis.topRecommendedTitles.map((t, i) => (
            <li
              key={i}
              className="flex items-start gap-2 rounded-lg border border-gold/30 bg-gold/5 px-3 py-2 text-sm text-white"
            >
              <span className="tabular text-xs font-bold text-gold mt-0.5 w-5">{i + 1}.</span>
              <span>{t}</span>
            </li>
          ))}
        </ol>
      </Card>

      {/* 롱테일 후보 키워드 */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-gold" /> 롱테일 후보 키워드
          </CardTitle>
          <CopyListButton
            items={data.analysis.rivalKeywords.map((k) => k.keyword)}
            label="키워드 복사"
          />
        </div>
        <CardDescription>본 키워드보다 경쟁이 약하면서 검색자가 실제 찾을 만한 변형</CardDescription>
        <ul className="mt-3 space-y-2">
          {data.analysis.rivalKeywords.map((r, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2"
            >
              <span className="tabular text-xs text-gray-500 mt-0.5 w-5">{i + 1}.</span>
              <div className="min-w-0 flex-1">
                <a
                  href={`/analyze/${encodeURIComponent(r.keyword)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-white hover:text-gold"
                  title="새 탭에서 분석 — 결과 페이지는 유지됩니다"
                >
                  {r.keyword}
                </a>
                {r.reason && (
                  <div className="mt-0.5 text-[11px] text-gray-400">{r.reason}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {/* SERP 원본 — Blog/Cafe/News */}
      <Card>
        <CardTitle>1page 실제 노출 글</CardTitle>
        <CardDescription>
          Blog {data.serp.blog.length} · Cafe {data.serp.cafe.length} · News {data.serp.news.length}
        </CardDescription>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <SerpColumn title="블로그" items={data.serp.blog} />
          <SerpColumn title="카페" items={data.serp.cafe} />
          <SerpColumn title="뉴스" items={data.serp.news} />
        </div>
      </Card>
    </div>
  );
}

function SerpColumn({ title, items }: { title: string; items: SerpItem[] }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
      <div className="text-xs font-bold text-gray-300 mb-2">
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-600">결과 없음</p>
      ) : (
        <ol className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-xs">
              <a
                href={it.link}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-200 hover:text-gold inline-flex items-start gap-1"
              >
                <span className="tabular text-gray-500 shrink-0">{i + 1}.</span>
                <span className="line-clamp-2">{it.title}</span>
                <ExternalLink className="h-2.5 w-2.5 shrink-0 mt-0.5 text-gray-600" />
              </a>
              {it.source && (
                <div className="ml-4 mt-0.5 text-[10px] text-gray-500 truncate">
                  {it.source}
                  {it.pubDate && ` · ${it.pubDate.slice(0, 10)}`}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─── 리스트 복사 버튼 (제목·키워드 모음 복사용) ──────────────

function CopyListButton({ items, label }: { items: string[]; label: string }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    if (items.length === 0) return;
    const text = items.join(', ');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        // ignore
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={items.length === 0}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
        copied
          ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-200'
          : 'border-gold/40 bg-gold/10 text-gold hover:bg-gold/20',
      )}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" /> 복사됨!
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" /> {label} ({items.length})
        </>
      )}
    </button>
  );
}
